
import { PushNotifications } from '@capacitor/push-notifications';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App } from '@capacitor/app';
import type { Router } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { isNativeApp, getPlatform, getOrCreateDeviceId } from './device';
import { deeplinkService } from './deeplink';

export { isNativeApp, getPlatform, getOrCreateDeviceId };

/**
 * Initialize Push Notifications
 * Note: Requires Firebase to be configured. Will fail gracefully if not set up.
 */
export const initPushNotifications = async (router?: Router) => {
    if (!isNativeApp()) return null;

    // Inicializa o serviço de deeplink com o router
    if (router) {
        deeplinkService.init(router);
    }

    try {
        console.log('[Push] Inicializando PushNotifications...');
        let permStatus = await PushNotifications.checkPermissions();
        console.log('[Push] Permissões atuais:', permStatus);
        if (permStatus.receive === 'prompt') {
            const result = await PushNotifications.requestPermissions();
            // Atualizar permStatus com o novo resultado após solicitar permissões
            permStatus = result;
            if (result.receive !== 'granted') {
                console.log('Push notification permission not granted');
                return null;
            }
        }

        if (permStatus.receive !== 'granted') {
            console.log('Push notification permission not granted');
            return null;
        }

        console.log('[Push] Permissões concedidas, registrando...');
        await PushNotifications.register();
        console.log('[Push] Registro chamado, aguardando token...');
        console.log('[Push] Se o token não chegar em 30s, verifique: SHA-256 no Firebase Console');

        // === Listener para evento nativo (iOS/Android) ===
        // Este listener captura notificações quando o app foi aberto por clique
        // (tratamento nativo no AppDelegate.swift e MainActivity.java)
        window.addEventListener('pushNotificationTapped', async (event: any) => {
            console.log('[Push] App aberto via notificação (nativo):', event.detail);
            const data = event.detail?.notificationData || {};
            const keys = Object.keys(data || {});
            if (!keys.length) {
                console.log('[DeepLinkDebug][PushDeeplink] pushNotificationTapped ignorado (payload vazio)');
                return;
            }
            console.log('[DeepLinkDebug][PushDeeplink] pushNotificationTapped payload keys', keys);
            const navigated = await deeplinkService.handleNotificationClick(data);
            if (!navigated) {
                console.warn('[Push] Não foi possível navegar para a página da notificação');
            }
        });

        // Listen for registration success
        PushNotifications.addListener('registration', (token) => {
            console.log('Push registration success, token: ', token.value);
            const deviceId = getOrCreateDeviceId();
            // NOTA: token.value é o APNs token (hexadecimal), não o FCM token
            // O Firebase irá converter e disparar o evento 'fcmTokenReady' com o FCM token correto
            localStorage.setItem('device_id', deviceId);
            window.dispatchEvent(new CustomEvent('pushTokenReceived', { detail: { token: token.value, deviceId } }));

            // NÃO registrar o APNs token - aguardar o FCM token via evento 'fcmTokenReady'
            // O FirebaseMessagingPlugin irá notificar quando receber o token correto
        });

        // Listen for registration errors
        PushNotifications.addListener('registrationError', (error) => {
            console.error('Push registration failed:', error);
        });

        // Listen for push notification received (app em foreground)
        PushNotifications.addListener('pushNotificationReceived', async (notification) => {
            console.log('Push notification received: ', notification);
            await deeplinkService.handleNotificationReceived(notification);
        });

        // Listen for push notification action performed (usuário clicou na notificação)
        PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
            console.log('Push notification action performed: ', notification);

            // Extrair dados da notificação
            const data = notification.notification?.data || {};

            // Tentar navegar usando o serviço de deeplink
            const navigated = await deeplinkService.handleNotificationClick(data);

            if (!navigated) {
                console.warn('[Push] Não foi possível navegar para a página da notificação');
            }
        });

        // === Verificar notificações entregues (app aberto por notificação) ===
        // Este método verifica se o app foi aberto clicando em uma notificação
        setTimeout(async () => {
            try {
                const delivered = await PushNotifications.getDeliveredNotifications();
                console.log('[Push] Notificações entregues:', delivered);
                // Notas:
                // - Quando o app é fechado e aberto por notificação, o tratamento nativo
                //   acima (pushNotificationTapped) é mais confiável.
                // - O listener 'pushNotificationActionPerformed' é acionado para
                //   notificações quando o app JÁ está aberto ou em background.
            } catch (e) {
                console.log('[Push] Erro ao verificar notificações entregues:', e);
            }
        }, 1000);

        return {
            addListener: PushNotifications.addListener.bind(PushNotifications),
            removeAllListeners: PushNotifications.removeAllListeners.bind(PushNotifications)
        };
    } catch (error) {
        // Firebase not configured or other initialization error
        console.warn('Push notifications not available:', error);
        return null;
    }
};

/**
 * Take photo with camera
 */
export const takePhoto = async () => {
    try {
        const image = await Camera.getPhoto({
            quality: 90,
            allowEditing: true,
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera
        });
        return image;
    } catch (error) {
        console.error('Error taking photo:', error);
        throw error;
    }
};

/**
 * Pick image from gallery
 */
export const pickFromGallery = async () => {
    try {
        const image = await Camera.getPhoto({
            quality: 90,
            allowEditing: true,
            resultType: CameraResultType.Uri,
            source: CameraSource.Photos
        });
        return image;
    } catch (error) {
        console.error('Error picking from gallery:', error);
        throw error;
    }
};

/**
 * Request geolocation permission first
 */
export const requestLocationPermission = async (): Promise<boolean> => {
    if (!isNativeApp()) return true; // Web doesn't need explicit permission request

    try {
        const permission = await Geolocation.checkPermissions();
        console.log('[Capacitor] Current location permission:', permission);

        if (permission.location === 'granted' || permission.coarseLocation === 'granted') {
            console.log('[Capacitor] Location permission already granted');
            return true;
        }

        // Always try to request permissions if not granted
        // On Android, 'denied' may mean never asked, so we should try requesting
        console.log('[Capacitor] Requesting location permission...');
        const result = await Geolocation.requestPermissions();
        console.log('[Capacitor] Permission request result:', result);

        if (result.location === 'granted' || result.coarseLocation === 'granted') {
            console.log('[Capacitor] Location permission granted after request');
            return true;
        }

        console.warn('[Capacitor] Location permission was denied after request');
        return false;
    } catch (error) {
        console.error('[Capacitor] Error requesting location permission:', error);
        return false;
    }
};

/**
 * Get current GPS position
 */
export const getCurrentPosition = async () => {
    try {
        console.log('[Capacitor] getCurrentPosition - isNativeApp:', isNativeApp());

        // Request permission first on native
        if (isNativeApp()) {
            console.log('[Capacitor] Requesting location permission...');
            const hasPermission = await requestLocationPermission();
            console.log('[Capacitor] Permission result:', hasPermission);
            if (!hasPermission) {
                throw new Error('Location permission not granted');
            }
        }

        console.log('[Capacitor] Calling Geolocation.getCurrentPosition...');
        const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000
        });
        console.log('[Capacitor] Got position:', position);
        console.log('[Capacitor] Coords:', position?.coords);
        return position;
    } catch (error) {
        console.error('[Capacitor] Error getting position:', error);
        throw error;
    }
};

/**
 * Watch position changes
 */
export const watchPosition = async (callback: (position: any) => void) => {
    // Request permission first on native
    if (isNativeApp()) {
        const hasPermission = await requestLocationPermission();
        if (!hasPermission) {
            throw new Error('Location permission not granted');
        }
    }

    return Geolocation.watchPosition({ enableHighAccuracy: true }, callback);
};

/**
 * Initialize app lifecycle listeners
 */
export const initAppListeners = (router: Router) => {
  if (!isNativeApp()) return;

    const normalizePathCandidate = (candidate: string) => {
        let normalized = String(candidate || '')
            .split('?')[0]
            .split('#')[0]
            .replace(/^\/+/, '');

        // Links web em produção podem vir com /app/ como prefixo.
        while (normalized.startsWith('app/')) {
            normalized = normalized.slice(4);
        }

        return normalized;
    };

    let lastHandledDeepLink = '';
    const waitForRouterReady = async () => {
        try {
            await router.isReady();
        } catch (error) {
            console.warn('[DeepLinkDebug] falha ao aguardar router.isReady', error);
        }
    };

    const handleDeepLink = async (rawUrl: string, source: 'appUrlOpen' | 'launchUrl' | 'nativeEvent') => {
        if (!rawUrl) return;

        // Evita processar duas vezes o mesmo link em sequência.
        if (rawUrl === lastHandledDeepLink) {
            console.log('[DeepLinkDebug] link duplicado ignorado', { source, rawUrl });
            return;
        }

        console.log('[DeepLinkDebug] link recebido', { source, rawUrl });

        try {
            const parsed = new URL(rawUrl);
            const hashPath = parsed.hash ? parsed.hash.replace(/^#/, '') : '';
            const candidates = Array.from(new Set([
                normalizePathCandidate(`${parsed.hostname}${parsed.pathname}`),
                normalizePathCandidate(parsed.pathname),
                normalizePathCandidate(hashPath)
            ])).filter(Boolean);

            console.log('[DeepLinkDebug] candidatos normalizados', { source, candidates });

            const resetPath = candidates.find(path => path.startsWith('reset-password/'));
            if (resetPath) {
                const token = resetPath.split('/')[1] || '';
                const email = parsed.searchParams.get('email') || '';

                if (token) {
                    await waitForRouterReady();
                    lastHandledDeepLink = rawUrl;
                    await router.push({
                        name: 'ResetPassword',
                        params: { token },
                        query: email ? { email } : {}
                    });
                    console.log('[DeepLinkDebug] rota reset-password aberta no app', { source, tokenLength: token.length, hasEmail: !!email });
                    return;
                }
            }

            const oficinaPath = candidates.find(path =>
                path.startsWith('usuario/oficina/') ||
                path.startsWith('app/usuario/oficina/')
            );
            if (oficinaPath) {
                const pathParts = oficinaPath.split('/');
                const oficinaId = pathParts[pathParts.length - 1];

                if (oficinaId) {
                    await waitForRouterReady();
                    lastHandledDeepLink = rawUrl;
                    await router.push({
                        name: 'UsuarioOficinaShow',
                        params: { id: oficinaId }
                    });
                    console.log('[DeepLinkDebug] rota oficina aberta no app', { source, oficinaId });
                    return;
                }
            }

            const forgotPath = candidates.find(path => path === 'forgot-password');
            if (forgotPath) {
                await waitForRouterReady();
                lastHandledDeepLink = rawUrl;
                await router.push({ name: 'ForgotPassword' });
                console.log('[DeepLinkDebug] rota forgot-password aberta no app', { source });
                return;
            }

            console.warn('[DeepLinkDebug] link não reconhecido pelo parser', { source, rawUrl, candidates });
        } catch (error) {
            console.warn('[DeepLinkDebug] URL inválida recebida', { source, rawUrl, error });
        }
    };

    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
            router.back();
        } else {
            App.exitApp();
        }
    });

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
        console.log('App state changed. Is active?', isActive);
        if (isActive && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('appResumed'));
        }
    });

    // Handle deep links
    App.addListener('appUrlOpen', async (event) => {
        const rawUrl = event?.url || '';
        await handleDeepLink(rawUrl, 'appUrlOpen');
    });

    // Deep link quando o app inicia "frio" pelo link (sem passar por appUrlOpen).
    App.getLaunchUrl()
        .then(async (launchData) => {
            const rawUrl = launchData?.url || '';
            if (!rawUrl) {
                console.log('[DeepLinkDebug] getLaunchUrl sem URL inicial');
                return;
            }

            await handleDeepLink(rawUrl, 'launchUrl');
        })
        .catch((error) => {
            console.warn('[DeepLinkDebug] falha ao ler getLaunchUrl', error);
        });

    // Fallback para links recebidos pelo código nativo (Android/iOS), para debug e redundância.
    if (typeof window !== 'undefined') {
        window.addEventListener('nativeDeepLinkReceived', async (event: Event) => {
            const customEvent = event as CustomEvent<{ url?: string }>
            const rawUrl = customEvent?.detail?.url || ''
            await handleDeepLink(rawUrl, 'nativeEvent')
        })

        // Fallback: se o nativo escreveu a URL antes de o listener registrar.
        const pendingDeepLinkUrl = (window as any).__pendingDeepLinkUrl as string | undefined
        if (pendingDeepLinkUrl) {
            delete (window as any).__pendingDeepLinkUrl
            handleDeepLink(pendingDeepLinkUrl, 'nativeEvent').catch((error) => {
                console.warn('[DeepLinkDebug] erro ao processar pendingDeepLinkUrl', error)
            })
        }
    }
};

/**
 * Hide splash screen
 */
export const hideSplash = async () => {
    if (isNativeApp()) {
        try {
            await SplashScreen.hide();
        } catch (error) {
            console.error('Error hiding splash:', error);
        }
    }
};

/**
 * Set status bar style
 */
export const setStatusBar = async (style: 'light' | 'dark') => {
    if (isNativeApp()) {
        try {
            await StatusBar.setStyle({ style: style === 'light' ? Style.Light : Style.Dark });
        } catch (error) {
            console.error('Error setting status bar:', error);
        }
    }
};

/**
 * Set status bar background color (Android only)
 */
export const setStatusBarColor = async (color: string) => {
    if (isNativeApp() && getPlatform() === 'android') {
        try {
            await StatusBar.setBackgroundColor({ color });
        } catch (error) {
            console.error('Error setting status bar color:', error);
        }
    }
};

/**
 * Set status bar overlay mode (content extends behind status bar)
 */
export const setStatusBarOverlay = async (overlay: boolean) => {
    if (isNativeApp()) {
        try {
            await StatusBar.setOverlaysWebView({ overlay });
        } catch (error) {
            console.error('Error setting status bar overlay:', error);
        }
    }
};

/**
 * Get status bar info including height
 */
export const getStatusBarInfo = async () => {
    if (isNativeApp()) {
        try {
            const info = await StatusBar.getInfo();
            return info;
        } catch (error) {
            console.error('Error getting status bar info:', error);
            return null;
        }
    }
    return null;
};

/**
 * Initialize status bar configuration for safe-area support
 * This should be called on app mount
 */
export const initializeStatusBar = async () => {
    if (!isNativeApp()) return;

    try {
        const platform = getPlatform();

        if (platform === 'android') {
            // Android: Ícones claros (brancos) em fundo escuro
            // Style.Dark = ícones brancos (para fundos escuros)
            await StatusBar.setStyle({ style: Style.Dark });

            // NÃO sobrepor o conteúdo com as barras do sistema (evita a navbar do Android
            // ficar em cima da bottom-nav do app). O WebView passa a respeitar os insets.
            await StatusBar.setOverlaysWebView({ overlay: false });
            await StatusBar.setBackgroundColor({ color: '#0D1117' });

            // Como não estamos sobrepondo, o WebView já vem ajustado para ficar abaixo
            // das barras do sistema. Alguns dispositivos Android ainda expõem
            // env(safe-area-inset-*) > 0 mesmo assim, o que fazia header e bottom-nav
            // somarem esse espaço uma segunda vez e "flutuarem" para dentro da tela.
            document.documentElement.style.setProperty('--android-status-bar-height', '0px');
            document.documentElement.style.setProperty('--safe-area-top', '0px');
            document.documentElement.style.setProperty('--safe-area-bottom', '0px');
            document.documentElement.classList.add('android-platform');
        } else if (platform === 'ios') {
            // iOS: Mantém o comportamento original (Style.Light já funcionava)
            await StatusBar.setStyle({ style: Style.Light });
            // iOS handles this via env(safe-area-inset-top) automatically with viewport-fit=cover
            document.documentElement.style.removeProperty('--safe-area-top');
            document.documentElement.style.removeProperty('--safe-area-bottom');
            document.documentElement.classList.add('ios-platform');
        }

        console.log('[Capacitor] Status bar initialized for platform:', platform);
    } catch (error) {
        console.error('[Capacitor] Error initializing status bar:', error);
    }
};
